import { RoboqlQuery } from "@/shared/domain/query";
import {
  fromSavedFilters,
  toSavedFilters,
  VIEW_SCHEME_V1,
  type SavedFilters,
  type ViewDefinitionV1,
  type ViewDisplay,
} from "@/shared/domain/views";
import type { DataTableSort } from "@/shared/foundation/primitives/Table";
import type { SortDirection } from "@/types";

import type { ActiveFiltersState, Filter, FilterMatchMode } from "./Filters";
import type { StaticFilterField } from "./Filters/catalog";

/**
 * The live state of a resource view — what this module reads on save.
 *
 * "View state" is the resource view's state; a *View* is the saved artifact.
 * Assembled from three owners: filters, table sort/page size, and grid columns.
 */
export interface ResourceViewState {
  filters: ActiveFiltersState;
  sort: DataTableSort | null;
  pageSize: number;
  visibleColumns: string[];
}

/**
 * What a saved View restores to.
 *
 * Not `ResourceViewState`: a RoboQL View restores its text but not the
 * transpiled spec, which only the server can produce. The caller resolves it
 * before handing filter state to the view.
 */
export interface RestoredViewState {
  filters: RestoredFilters;
  sort: DataTableSort | null;
  /** `null` when the View recorded no preference; apply the table's own default. */
  pageSize: number | null;
  /** `null` when the View recorded no preference; apply the table's own defaults. */
  visibleColumns: string[] | null;
}

/**
 * Filter state as restored from a View. The `"roboql"` arm carries the query
 * alone — the missing `spec` is what forces consumers to convert before
 * searching.
 */
export type RestoredFilters =
  | { type: "none" }
  | { type: "structured"; filters: Filter[]; matchMode: FilterMatchMode }
  | { type: "roboql"; query: RoboqlQuery };

/**
 * Capture the current resource view state as a saveable View definition.
 *
 * Records the author's controls, not the query they translate to: translation
 * resolves a relative date to fixed instants, so re-translating on load is what
 * keeps a relative filter relative. A RoboQL View saves only its text.
 */
export function viewStateToDefinition(
  state: ResourceViewState,
): ViewDefinitionV1 {
  return {
    scheme: VIEW_SCHEME_V1,
    roboql:
      state.filters.type === "roboql" ? state.filters.query.toString() : null,
    filters:
      state.filters.type === "structured"
        ? toSavedFilters(
            state.filters.filters.map(toViewWireFilter),
            state.filters.matchMode,
          )
        : null,
    display: {
      visible_columns: state.visibleColumns,
      ...sortToWire(state.sort),
      page_size: state.pageSize,
    },
  };
}

/**
 * Restore what a saved View recorded. Page size and columns come back as `null`
 * when the View recorded no preference, so the caller applies its own defaults.
 * `fields` are the View's table's filter rows (`staticFilterFieldsFor`), whose
 * option labels the restored chips show.
 *
 * @throws If written under an unknown schema — reading it as v1 would apply a
 * filter set the author never chose.
 */
export function definitionToViewState(
  definition: ViewDefinitionV1,
  fields: readonly StaticFilterField[] = [],
): RestoredViewState {
  if (definition.scheme !== VIEW_SCHEME_V1) {
    throw new Error(
      `Cannot read a view saved as '${definition.scheme}'. This client understands '${VIEW_SCHEME_V1}'.`,
    );
  }

  return {
    filters: readFilters(definition, fields),
    sort: readSort(definition.display),
    pageSize: definition.display.page_size,
    visibleColumns: definition.display.visible_columns.length
      ? definition.display.visible_columns
      : null,
  };
}

function readFilters(
  definition: ViewDefinitionV1,
  fields: readonly StaticFilterField[],
): RestoredFilters {
  // RoboQL first: a definition carrying both is malformed, and the typed query
  // is the better guess at intent.
  if (definition.roboql !== null) {
    return { type: "roboql", query: RoboqlQuery.from(definition.roboql) };
  }
  if (definition.filters !== null) {
    return {
      type: "structured",
      ...restoreFilters(definition.filters, fields),
    };
  }
  return { type: "none" };
}

/**
 * Project `set` and `enum` down to the bare values the wire takes.
 *
 * `roboto.query.SetFilter` and `EnumFilter` declare `values: list[str]` and forbid extra keys,
 * so the `{ value, label }` options the UI holds are refused on save. Only `identity` keeps
 * its labels, deliberately: a principal id is not a name a reader recognises, and unlike these
 * two its label cannot be reconstructed.
 *
 * Local to Views rather than folded into `toSavedFilters`. That helper is shared with saved
 * dashboards, whose read path validates through `savedFiltersSchema` — which declares these
 * values as labeled options. Projecting there would make dashboards write a shape its own
 * reader rejects. The two schemas disagreeing about `set` and `enum` is a real problem, but it
 * belongs to whoever owns that boundary, not to a View serializer.
 */
function toViewWireFilter(filter: Filter): Filter {
  if (filter.type === "set" || filter.type === "enum") {
    // The stored shape is not a `Filter` for these two; the cast marks that
    // boundary rather than hiding it.
    return {
      ...filter,
      values: filter.values.map((option) => option.value),
    } as unknown as Filter;
  }

  return filter;
}

/**
 * Rebuild the options the UI needs from the bare values that were stored.
 *
 * A value takes its label from the matching option of its field in `fields`, the label it
 * displayed as when it was picked. A value with no such option is its own label: a `set`
 * field's options are looked up at runtime, and an option may have been dropped since the
 * View was saved. Tolerates labeled options too, so a View written before the projection
 * existed still restores.
 */
function restoreFilters(
  saved: SavedFilters,
  fields: readonly StaticFilterField[],
): {
  filters: Filter[];
  matchMode: FilterMatchMode;
} {
  const restored = fromSavedFilters(saved);
  return {
    ...restored,
    filters: restored.filters.map((filter) => {
      if (filter.type !== "set" && filter.type !== "enum") {
        return filter;
      }

      const labels = optionLabels(fields, filter.field);
      const stored = filter.values as unknown as (
        string | LabeledOptionValue
      )[];
      return {
        ...filter,
        values: stored.map((value) =>
          typeof value === "string"
            ? { value, label: labels.get(value) ?? value }
            : value,
        ),
      };
    }),
  };
}

/** Each labeled option of the `enum` field named `field`, by value. */
function optionLabels(
  fields: readonly StaticFilterField[],
  field: string,
): ReadonlyMap<string, string> {
  const definition = fields.find(
    (candidate) => candidate.field === field && candidate.type === "enum",
  );
  const labels = new Map<string, string>();
  if (definition?.type === "enum") {
    for (const option of definition.options) {
      if (typeof option !== "string") {
        labels.set(option.value, option.label);
      }
    }
  }
  return labels;
}

/** The `{ value, label }` pair a committed option-backed filter holds. */
interface LabeledOptionValue {
  value: string;
  label: string;
}

function toWireDirection(direction: DataTableSort["direction"]): SortDirection {
  return direction === "desc" ? "DESC" : "ASC";
}

/**
 * The wire fields a table sort is stored as on a View. An unsorted table is
 * stored as `unsorted`, because a `null` `sort_by` alone means the list's
 * default sort.
 */
export function sortToWire(sort: DataTableSort | null): {
  sort_by: string | null;
  sort_direction: SortDirection | null;
  unsorted: boolean;
} {
  return {
    sort_by: sort?.columnId ?? null,
    sort_direction: sort ? toWireDirection(sort.direction) : null,
    unsorted: sort === null,
  };
}

/** The table sort two wire fields describe; `null` when no column is named. */
export function sortFromWire(
  sortBy: string | null | undefined,
  sortDirection: SortDirection | null | undefined,
): DataTableSort | null {
  if (!sortBy) {
    return null;
  }
  return {
    columnId: sortBy,
    direction: sortDirection === "DESC" ? "desc" : "asc",
  };
}

function readSort(display: ViewDisplay): DataTableSort | null {
  return sortFromWire(display.sort_by, display.sort_direction);
}
